import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3013/api';

// Generate next 14 days
const getNext14Days = () => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    days.push(date);
  }
  return days;
};

// Format date helper
const formatDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return {
    day: days[date.getDay()],
    date: date.getDate(),
    month: months[date.getMonth()],
    dateStr: date.toDateString(),
  };
};

// Group events by date (handles multi-day and recurring daily events)
const groupEventsByDate = (events) => {
  const grouped = {};
  
  events.forEach(event => {
    if (!event.startDate) return;
    
    const startDate = new Date(event.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
    endDate.setHours(0, 0, 0, 0);
    
    if (startDate.toDateString() === 'Invalid Date') return;
    
    // Calculate total days of the event
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const isMultiDay = totalDays > 1;
    const isRecurringDaily = event.isRecurringDaily === true;
    
    // Add event to each day it spans
    for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + (dayNum - 1));
      const dateKey = currentDate.toDateString();
      
      if (!grouped[dateKey]) grouped[dateKey] = [];
      
      // For recurring daily events, use daily times
      // For continuous multi-day events, use original start/end times
      const displayStartTime = isRecurringDaily && event.dailyStartTime 
        ? event.dailyStartTime 
        : event.startTime;
      const displayEndTime = isRecurringDaily && event.dailyEndTime 
        ? event.dailyEndTime 
        : event.endTime;
      
      // Create event entry with day info for multi-day events
      const eventEntry = {
        ...event,
        type: 'event',
        isMultiDay,
        isRecurringDaily,
        dayNumber: dayNum,
        totalDays: event.totalDays || totalDays,
        displayStartTime,
        displayEndTime,
        // Show different labels based on event type
        dayLabel: isRecurringDaily 
          ? `Day ${dayNum}/${event.totalDays || totalDays}`
          : (isMultiDay ? (
              dayNum === 1 ? 'Start' : 
              dayNum === totalDays ? 'Bhog' : 
              `Day ${dayNum}`
            ) : null),
      };
      
      grouped[dateKey].push(eventEntry);
    }
  });
  
  return grouped;
};

// Group holidays by date
const groupHolidaysByDate = (holidays) => {
  const grouped = {};
  holidays.forEach(holiday => {
    if (!holiday.date) return;
    const dateKey = new Date(holiday.date).toDateString();
    if (dateKey === 'Invalid Date') return;
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push({ ...holiday, type: 'holiday' });
  });
  return grouped;
};

// Merge events and holidays
const mergeByDate = (events, holidays) => {
  const merged = { ...events };
  Object.keys(holidays).forEach(dateKey => {
    if (!merged[dateKey]) merged[dateKey] = [];
    merged[dateKey] = [...holidays[dateKey], ...merged[dateKey]];
  });
  return merged;
};

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      // No auth required - public endpoints
      const [eventsRes, holidaysRes] = await Promise.all([
        axios.get(`${API_URL}/sab/events/public-calendar`),
        axios.get(`${API_URL}/sab/sikh-holidays/public-calendar`),
      ]);
      
      setEvents(eventsRes.data || []);
      setHolidays(holidaysRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll every 60 seconds for updates
    const interval = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const days = getNext14Days();
  const groupedEvents = groupEventsByDate(events);
  const groupedHolidays = groupHolidaysByDate(holidays);
  const allItems = mergeByDate(groupedEvents, groupedHolidays);
  const today = new Date().toDateString();
  const tomorrow = new Date(Date.now() + 86400000).toDateString();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <img src="https://brantsikhs.com/logo.png" alt="SAB" style={styles.logo} />
        <div style={styles.headerTitle}>Upcoming Programs</div>
        <div style={styles.headerSub}>ਆਉਣ ਵਾਲੇ ਪ੍ਰੋਗਰਾਮ</div>
      </div>

      {/* Calendar Grid - 5 columns x 3 rows (14 days + 1 branding card) */}
      <div style={styles.grid}>
        {days.map((date, index) => {
          const { day, date: dateNum, month, dateStr } = formatDate(date);
          const dayItems = allItems[dateStr] || [];
          const isToday = dateStr === today;
          const isTomorrow = dateStr === tomorrow;
          const hasItems = dayItems.length > 0;
          const hasHoliday = dayItems.some(item => item.type === 'holiday');

          return (
            <div
              key={index}
              style={{
                ...styles.card,
                ...(isToday && styles.todayCard),
                ...(isTomorrow && !isToday && styles.tomorrowCard),
                ...(hasHoliday && styles.holidayCard),
                ...(!hasItems && styles.emptyCard),
              }}
            >
              {/* Date Header */}
              <div style={{
                ...styles.dateHeader,
                background: hasHoliday ? '#7c3aed' : isToday ? '#F97316' : isTomorrow ? '#3b82f6' : '#1e293b',
              }}>
                <span style={styles.dateNum}>{dateNum}</span>
                <div style={styles.dateMeta}>
                  <span style={styles.dayName}>{day}</span>
                  <span style={styles.monthName}>{month}</span>
                </div>
                {isToday && <span style={styles.badge}>TODAY</span>}
                {isTomorrow && !isToday && <span style={{...styles.badge, background: '#fff', color: '#3b82f6'}}>TMR</span>}
              </div>

              {/* Items (Holidays + Events) */}
              <div style={styles.eventsList}>
                {dayItems.length === 0 ? (
                  <div style={styles.noEvent}>
                    <span style={styles.khandaIcon}>☬</span>
                    <span style={styles.waheguruText}>ਸਤਿਨਾਮ ਵਾਹਿਗੁਰੂ</span>
                  </div>
                ) : (
                  dayItems.slice(0, 3).map((item, i) => (
                    item.type === 'holiday' ? (
                      <div
                        key={item._id || i}
                        style={{
                          ...styles.holiday,
                          background: `${item.color}20`,
                          borderLeftColor: item.color || '#7c3aed',
                        }}
                      >
                        <span style={styles.holidayIcon}>{item.icon}</span>
                        <span style={styles.holidayName}>{item.name}</span>
                      </div>
                    ) : (
                      <div
                        key={`${item._id}-${item.dayNumber || 1}` || i}
                        style={{
                          ...styles.event,
                          borderLeftColor: item.programTypeColor || '#F97316',
                        }}
                      >
                        <div style={styles.eventTimeCol}>
                          {item.isRecurringDaily ? (
                            // Recurring daily events - show day badge and daily time
                            <>
                              <span style={styles.dayBadge}>{item.dayLabel}</span>
                              <span style={styles.multiDayTime}>
                                {item.displayStartTime}
                              </span>
                            </>
                          ) : item.isMultiDay ? (
                            // Continuous multi-day events
                            <>
                              <span style={styles.dayBadge}>{item.dayLabel}</span>
                              {(item.dayNumber === 1 || item.dayNumber === item.totalDays) && (
                                <span style={styles.multiDayTime}>
                                  {item.dayNumber === 1 ? item.startTime : item.endTime}
                                </span>
                              )}
                            </>
                          ) : (
                            // Single day events
                            <span style={styles.eventTime}>{item.startTime}</span>
                          )}
                        </div>
                        <div style={styles.eventInfo}>
                          <span style={styles.eventName}>{item.programTypeName || 'Event'}</span>
                          <span style={styles.eventBooking}>{item.bookingName}</span>
                        </div>
                      </div>
                    )
                  ))
                )}
                {dayItems.length > 3 && (
                  <div style={styles.moreEvents}>+{dayItems.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Codeforce Branding Card */}
        <div style={styles.brandingCard}>
          <div style={styles.brandingContent}>
            <img 
              src="https://codeforce.ca/logo.png" 
              alt="Codeforce" 
              style={styles.brandingLogo}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={styles.brandingText}>
              <span style={styles.brandingTitle}>Designed & Developed by</span>
              <span style={styles.brandingCompany}>CODEFORCE</span>
              <span style={styles.brandingEmail}>info@codeforce.ca</span>
            </div>
            <div style={styles.brandingGuidance}>
              Under guidance of Sikh Association of Brantford
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ ॥ ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ ॥
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    overflow: 'hidden',
    padding: 16,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 24px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    marginBottom: 14,
  },
  logo: {
    width: 48,
    height: 48,
  },
  headerTitle: {
    color: '#F97316',
    fontSize: 28,
    fontWeight: 700,
  },
  headerSub: {
    color: '#94a3b8',
    fontSize: 16,
    marginLeft: 'auto',
  },
  grid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(3, 1fr)',
    gap: 12,
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  todayCard: {
    border: '3px solid #F97316',
    boxShadow: '0 0 20px rgba(249, 115, 22, 0.4)',
  },
  tomorrowCard: {
    border: '3px solid #3b82f6',
  },
  holidayCard: {
    border: '2px solid #7c3aed',
    boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)',
  },
  emptyCard: {
    opacity: 0.7,
  },
  dateHeader: {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#fff',
  },
  dateNum: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1,
  },
  dateMeta: {
    display: 'flex',
    flexDirection: 'column',
  },
  dayName: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: 1.2,
  },
  monthName: {
    fontSize: 12,
    opacity: 0.8,
    lineHeight: 1.2,
  },
  badge: {
    marginLeft: 'auto',
    background: '#fff',
    color: '#F97316',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 4,
  },
  eventsList: {
    flex: 1,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    overflow: 'hidden',
  },
  event: {
    display: 'flex',
    gap: 8,
    padding: '5px 8px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 6,
    borderLeft: '3px solid #F97316',
    alignItems: 'center',
  },
  eventTimeCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 42,
    gap: 2,
  },
  eventTime: {
    color: '#F97316',
    fontSize: 12,
    fontWeight: 600,
  },
  dayBadge: {
    background: '#F97316',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  multiDayTime: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: 500,
  },
  eventInfo: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  eventName: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
  },
  eventBooking: {
    color: '#94a3b8',
    fontSize: 10,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
  },
  noEvent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: 8,
  },
  khandaIcon: {
    fontSize: 42,
    color: '#F97316',
    opacity: 0.8,
  },
  waheguruText: {
    fontSize: 14,
    color: '#94a3b8',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  moreEvents: {
    color: '#64748b',
    fontSize: 11,
    textAlign: 'center',
  },
  holiday: {
    display: 'flex',
    gap: 6,
    padding: '5px 8px',
    borderRadius: 6,
    borderLeft: '3px solid #7c3aed',
    alignItems: 'center',
  },
  holidayIcon: {
    fontSize: 14,
  },
  holidayName: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  footer: {
    marginTop: 12,
    padding: '10px 16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    textAlign: 'center',
    color: '#F97316',
    fontSize: 16,
  },
  loadingText: {
    color: '#fff',
    fontSize: 20,
    margin: 'auto',
  },
  brandingCard: {
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 12,
  },
  brandingLogo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    filter: 'brightness(1.1)',
  },
  brandingText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  brandingTitle: {
    color: '#94a3b8',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  brandingCompany: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 2,
  },
  brandingEmail: {
    color: '#F97316',
    fontSize: 12,
    fontWeight: 500,
  },
  brandingGuidance: {
    color: '#64748b',
    fontSize: 10,
    textAlign: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(255,255,255,0.1)',
    lineHeight: 1.3,
  },
};

export default EventCalendar;
